import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ContaPagarReceberDTO } from '../../../models/dashboard/conta-pagar-receber.model';
import { ContaDTO } from '../../../models/gerenciar-contas/conta.model';
import { PeriodoFiltro } from '../../../models/dashboard/periodo-filtro.model';
import { PRESETS_PERIODO_DASHBOARD_CONTAS } from '../../../shared/utils/periodo.utils';

interface SelectItem { label: string; value: any; flagUrl?: string; }

@Component({
  selector: 'app-dashboard-contas',
  templateUrl: './dashboard-contas.component.html',
  styleUrls: ['./dashboard-contas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardContasComponent implements OnInit, OnDestroy, OnChanges {

  // Opcoes do filtro de periodo do dashboard de contas (7 presets)
  readonly opcoesPeriodo = PRESETS_PERIODO_DASHBOARD_CONTAS;

  @Input() tipo: 'pagar' | 'receber' = 'receber';

  @Input() contas: ContaPagarReceberDTO[] = [];
  @Input() totalAberto = 0;
  @Input() totalPago = 0;
  @Input() loading = false;
  @Input() currencySymbol = 'PYG';
  @Input() currencyDigits = '1.0-0';
  @Input() currencyLocale = 'es-PY';

  // Filtros independentes por secao (replica filtroContaPagar/filtroContaReceber do mobile)
  @Input() contas_disponiveis: ContaDTO[] = [];
  @Input() contaSelecionadaLocal: ContaDTO | null = null;
  @Input() periodoSelecionadoLocal: PeriodoFiltro | null = null;

  @Output() mostrarMaisClicado = new EventEmitter<void>();
  @Output() contaClicada = new EventEmitter<ContaPagarReceberDTO>();
  // Emite quando a conta ou periodo local e alterado
  @Output() contaLocalChanged = new EventEmitter<ContaDTO | null>();
  @Output() periodoLocalChanged = new EventEmitter<PeriodoFiltro>();
  // Emite quando o usuario clica nos boxes de totais para navegar com filtros
  @Output() totalAbertoClicado = new EventEmitter<void>();
  @Output() totalPagoClicado = new EventEmitter<void>();

  texts = {
    contas_a_pagar: '',
    contas_a_receber: '',
    a_pagar: '',
    pagos: '',
    a_receber: '',
    recebidos: '',
    nome: '',
    primeiro_vencimento: '',
    recebido_pago: '',
    devolvido: '',
    mostrar_mais: '',
    filtrar_conta: '',
    filtrar_periodo: '',
    sem_registros: '',
    filtro_por_vencimento: ''
  };

  // Opcoes dos dropdowns de filtro
  contaOptions: SelectItem[] = [];

  private langChangeSub!: Subscription;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadTranslations();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contas_disponiveis']) {
      this.buildContaOptions();
    }
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  onContaSelect(event: any): void {
    this.contaLocalChanged.emit(event.value);
  }

  onPeriodoChanged(periodo: PeriodoFiltro): void {
    this.periodoLocalChanged.emit(periodo);
  }

  private loadTranslations(): void {
    this.texts = {
      contas_a_pagar: this.translate.instant('dashboard.texts.contas_a_pagar'),
      contas_a_receber: this.translate.instant('dashboard.texts.contas_a_receber'),
      a_pagar: this.translate.instant('dashboard.texts.a_pagar'),
      pagos: this.translate.instant('dashboard.texts.pagos'),
      a_receber: this.translate.instant('dashboard.texts.a_receber'),
      recebidos: this.translate.instant('dashboard.texts.recebidos'),
      nome: this.translate.instant('dashboard.texts.nome'),
      primeiro_vencimento: this.translate.instant('dashboard.texts.primeiro_vencimento'),
      recebido_pago: this.translate.instant('dashboard.texts.recebido_pago'),
      devolvido: this.translate.instant('dashboard.texts.devolvido'),
      mostrar_mais: this.translate.instant('dashboard.texts.mostrar_mais'),
      filtrar_conta: this.translate.instant('dashboard.texts.filtrar_conta'),
      filtrar_periodo: this.translate.instant('dashboard.texts.filtrar_periodo'),
      sem_registros: this.translate.instant('dashboard.texts.sem_registros'),
      filtro_por_vencimento: this.translate.instant('dashboard.texts.filtro_por_vencimento')
    };
    // Reconstruir opcoes ao mudar idioma
    this.buildContaOptions();
  }

  private buildContaOptions(): void {
    this.contaOptions = (this.contas_disponiveis || []).map(c => ({
      label: c.tituloDaConta,
      value: c,
      flagUrl: this.getFlagUrlByMoedaEnum(c.moeda)
    }));
  }

  getFlagUrlByMoedaEnum(moeda: any): string {
    const moedaEnum = (typeof moeda === 'object' && moeda?.enum ? moeda.enum : moeda || '').toString().toUpperCase();
    switch (moedaEnum) {
      case 'GUARANI': return 'assets/images/paraguay-flag.jpg';
      case 'REAL': return 'assets/images/br-flag.svg';
      case 'DOLAR': return 'assets/images/usa-flag.svg';
      case 'PESO': return 'assets/images/argentina-flag.svg';
      default: return '';
    }
  }

  get titulo(): string {
    return this.tipo === 'pagar' ? this.texts.contas_a_pagar : this.texts.contas_a_receber;
  }

  get labelAberto(): string {
    return this.tipo === 'pagar' ? this.texts.a_pagar : this.texts.a_receber;
  }

  get labelPago(): string {
    return this.tipo === 'pagar' ? this.texts.pagos : this.texts.recebidos;
  }

  get colunaValor(): string {
    return this.tipo === 'pagar' ? this.texts.a_pagar : this.texts.a_receber;
  }

  get colunaRecebido(): string {
    return this.tipo === 'pagar' ? this.texts.pagos : this.texts.recebidos;
  }

  get temDevolvido(): boolean {
    return this.contas.some(c => c.valorDevolvido > 0);
  }

  formatarData(data: string): string {
    if (!data) return '';
    const d = new Date(data);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  onContaClicada(conta: ContaPagarReceberDTO): void {
    this.contaClicada.emit(conta);
  }
}
